/**
 * Receipt Form Page - Create/Edit Incoming Fuel Receipt
 * Leviathan - Fuel Jobber Back Office
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface Supplier {
  id: string;
  supplierNumber: string;
  name: string;
  paymentTerms: number | null;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  unit: string;
  costPerUnit: number;
}

interface Tank {
  id: string;
  tankNumber: string;
  name: string;
  productId: string | null;
  capacity: number;
  currentLevel: number;
}

interface ReceiptItem {
  productId: string;
  productName: string;
  tankId: string;
  orderedQuantity: number;
  receivedQuantity: number;
  unit: string;
  unitCost: number;
  lineTotal: number;
  notes: string;
}

interface FormData {
  supplierId: string;
  receiptDate: string;
  deliveryDate: string;
  supplierInvoice: string;
  bolNumber: string;
  poNumber: string;
  deliveredBy: string;
  carrierName: string;
  vehicleNumber: string;
  freightCharge: number;
  otherCharges: number;
  notes: string;
  items: ReceiptItem[];
}

export default function ReceiptFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [tanks, setTanks] = useState<Tank[]>([]);

  const [formData, setFormData] = useState<FormData>({
    supplierId: '',
    receiptDate: new Date().toISOString().split('T')[0] || '',
    deliveryDate: new Date().toISOString().split('T')[0] || '',
    supplierInvoice: '',
    bolNumber: '',
    poNumber: '',
    deliveredBy: '',
    carrierName: '',
    vehicleNumber: '',
    freightCharge: 0,
    otherCharges: 0,
    notes: '',
    items: [],
  });

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
    fetchTanks();
    if (isEditMode) {
      fetchReceipt();
    }
  }, [id]);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/suppliers?isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory?category=FUEL&isActive=true&limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchTanks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tanks?status=ACTIVE`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTanks(data.tanks || []);
      }
    } catch (err) {
      console.error('Failed to fetch tanks:', err);
    }
  };

  const fetchReceipt = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/receipts/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const receipt = data.receipt;
        setFormData({
          supplierId: receipt.supplierId,
          receiptDate: receipt.receiptDate?.split('T')[0] || '',
          deliveryDate: receipt.deliveryDate?.split('T')[0] || '',
          supplierInvoice: receipt.supplierInvoice || '',
          bolNumber: receipt.bolNumber || '',
          poNumber: receipt.poNumber || '',
          deliveredBy: receipt.deliveredBy || '',
          carrierName: receipt.carrierName || '',
          vehicleNumber: receipt.vehicleNumber || '',
          freightCharge: receipt.freightCharge || 0,
          otherCharges: receipt.otherCharges || 0,
          notes: receipt.notes || '',
          items: receipt.items.map((item: ReceiptItem & { product?: { name: string } }) => ({
            productId: item.productId,
            productName: item.product?.name || '',
            tankId: item.tankId || '',
            orderedQuantity: item.orderedQuantity || 0,
            receivedQuantity: item.receivedQuantity || 0,
            unit: item.unit || 'gallon',
            unitCost: item.unitCost || 0,
            lineTotal: item.lineTotal || 0,
            notes: item.notes || '',
          })),
        });
      } else {
        setError('Receipt not found');
      }
    } catch (err) {
      setError('Failed to load receipt');
    } finally {
      setIsLoading(false);
    }
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          productId: '',
          productName: '',
          tankId: '',
          orderedQuantity: 0,
          receivedQuantity: 0,
          unit: 'gallon',
          unitCost: 0,
          lineTotal: 0,
          notes: '',
        },
      ],
    }));
  };

  const updateItem = (index: number, field: string, value: string | number) => {
    setFormData((prev) => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      if (!currentItem) return prev;

      const item = { ...currentItem, [field]: value };

      // If product changed, update cost and name
      if (field === 'productId') {
        const product = products.find((p) => p.id === value);
        if (product) {
          item.productName = product.name;
          item.unitCost = product.costPerUnit;
          item.unit = product.unit;
          // Auto-select matching tank
          const matchingTank = tanks.find((t) => t.productId === value);
          if (matchingTank) {
            item.tankId = matchingTank.id;
          }
        }
      }

      // Recalculate line total
      item.lineTotal = item.receivedQuantity * item.unitCost;
      newItems[index] = item;

      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.lineTotal, 0);
    const total = subtotal + formData.freightCharge + formData.otherCharges;
    const totalGallons = formData.items.reduce((sum, item) => sum + item.receivedQuantity, 0);
    return { subtotal, total, totalGallons };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.supplierId) {
      setError('Please select a supplier');
      return;
    }

    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    if (formData.items.some((item) => !item.productId || !item.tankId || item.receivedQuantity <= 0)) {
      setError('Please complete all item fields (product, tank, quantity)');
      return;
    }

    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/receipts/${id}` : `${API_URL}/api/receipts`;

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate('/receipts');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to save receipt');
      }
    } catch (err) {
      setError('Failed to save receipt');
    } finally {
      setIsSaving(false);
    }
  };

  const { subtotal, total, totalGallons } = calculateTotals();

  const getTanksForProduct = (productId: string) => {
    if (!productId) return tanks;
    return tanks.filter((t) => !t.productId || t.productId === productId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading receipt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Receipt' : 'New Receipt'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify receipt details' : 'Record incoming fuel delivery'}
              </p>
            </div>
            <button
              onClick={() => navigate('/receipts')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider"
            >
              ← Back to Receipts
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Supplier & Dates */}
        <Panel title="Receipt Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Supplier *
              </label>
              <select
                value={formData.supplierId}
                onChange={(e) => setFormData((prev) => ({ ...prev, supplierId: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                required
              >
                <option value="">Select Supplier...</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.supplierNumber})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Receipt Date
              </label>
              <input
                type="date"
                value={formData.receiptDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, receiptDate: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Delivery Date
              </label>
              <input
                type="date"
                value={formData.deliveryDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, deliveryDate: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Supplier Invoice #
              </label>
              <input
                type="text"
                value={formData.supplierInvoice}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, supplierInvoice: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                BOL #
              </label>
              <input
                type="text"
                value={formData.bolNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, bolNumber: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                PO #
              </label>
              <input
                type="text"
                value={formData.poNumber}
                onChange={(e) => setFormData((prev) => ({ ...prev, poNumber: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Carrier Info */}
        <Panel title="Carrier Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Carrier Name
              </label>
              <input
                type="text"
                value={formData.carrierName}
                onChange={(e) => setFormData((prev) => ({ ...prev, carrierName: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Delivered By (Driver)
              </label>
              <input
                type="text"
                value={formData.deliveredBy}
                onChange={(e) => setFormData((prev) => ({ ...prev, deliveredBy: e.target.value }))}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Vehicle / Truck #
              </label>
              <input
                type="text"
                value={formData.vehicleNumber}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, vehicleNumber: e.target.value }))
                }
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Items */}
        <Panel title="Received Items">
          <div className="space-y-4">
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-text-tertiary font-mono">
                No items added yet
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="border-b border-panel-border">
                    <tr>
                      <th className="text-left px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Product
                      </th>
                      <th className="text-left px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono">
                        Dest. Tank
                      </th>
                      <th className="text-right px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Ordered
                      </th>
                      <th className="text-right px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Received
                      </th>
                      <th className="text-right px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-24">
                        Cost/Unit
                      </th>
                      <th className="text-right px-3 py-2 text-text-secondary text-xs uppercase tracking-widest font-mono w-28">
                        Total
                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index} className="border-b border-panel-border">
                        <td className="px-3 py-2">
                          <select
                            value={item.productId}
                            onChange={(e) => updateItem(index, 'productId', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                          >
                            <option value="">Select Product...</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={item.tankId}
                            onChange={(e) => updateItem(index, 'tankId', e.target.value)}
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm"
                          >
                            <option value="">Select Tank...</option>
                            {getTanksForProduct(item.productId).map((tank) => (
                              <option key={tank.id} value={tank.id}>
                                {tank.tankNumber} - {tank.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.orderedQuantity || ''}
                            onChange={(e) =>
                              updateItem(index, 'orderedQuantity', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.receivedQuantity || ''}
                            onChange={(e) =>
                              updateItem(index, 'receivedQuantity', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            value={item.unitCost || ''}
                            onChange={(e) =>
                              updateItem(index, 'unitCost', parseFloat(e.target.value) || 0)
                            }
                            min="0"
                            step="0.001"
                            className="w-full bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-amber font-mono font-semibold">
                            ${item.lineTotal.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-error-red hover:text-error-red/80 font-mono text-sm"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button
              type="button"
              onClick={addItem}
              className="w-full border border-dashed border-panel-border hover:border-amber text-text-secondary hover:text-amber py-3 font-mono text-sm transition-colors"
            >
              + Add Item
            </button>
          </div>
        </Panel>

        {/* Totals & Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Panel title="Notes">
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              rows={4}
              placeholder="Additional notes..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
            />
          </Panel>

          <Panel title="Receipt Totals">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary font-mono">Total Gallons</span>
                <span className="text-text-primary font-mono">
                  {totalGallons.toLocaleString()} gal
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-text-secondary font-mono">Subtotal</span>
                <span className="text-text-primary font-mono">${subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Freight</span>
                  <input
                    type="number"
                    value={formData.freightCharge || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        freightCharge: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">
                  ${formData.freightCharge.toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <span className="text-text-secondary font-mono">Other</span>
                  <input
                    type="number"
                    value={formData.otherCharges || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        otherCharges: parseFloat(e.target.value) || 0,
                      }))
                    }
                    min="0"
                    step="0.01"
                    className="w-24 bg-background border border-panel-border focus:border-amber px-2 py-1 text-text-primary font-mono text-sm text-right"
                  />
                </div>
                <span className="text-text-primary font-mono">
                  ${formData.otherCharges.toFixed(2)}
                </span>
              </div>

              <div className="border-t border-panel-border pt-4 flex justify-between items-center">
                <span className="text-text-primary font-mono font-semibold text-lg">TOTAL</span>
                <span
                  className="text-amber font-mono font-bold text-2xl"
                  style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.5)' }}
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </Panel>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/receipts')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Receipt' : 'Create Receipt'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
